import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { levelEntityApi } from '../api/endpoints/levelEntity';
import { generateIdempotencyKey } from '../lib/idempotency';
import type {
    LevelEntityListItemLocal,
    LevelNameLocal,
    CreateLevelEntityRequestLocal,
    UpdateLevelEntityRequestLocal,
} from '../api/types/LevelEntityLocal';

/**
 * Hooks TanStack Query de las entidades de NIVEL genéricas
 * (`/tenants/{tenantId}/levels/{level}/entities`). queryKey
 * `['tenant', tenantId, 'levels', level, parentId ?? null]` — el `parentId` participa de la key
 * porque LEVEL_2/LEVEL_3 se listan filtrados por padre (cada padre es una caché distinta).
 *
 * Las mutations invalidan el listado de ESE nivel del tenant (prefijo
 * `['tenant', tenantId, 'levels', level]`, sin `parentId` → cubre todas las cachés por padre).
 * El orden de presentación (alfabético por `displayName`, default del proyecto) lo resuelve la UI.
 */

function levelKeyPrefix(tenantId: string | undefined, level: LevelNameLocal) {
    return ['tenant', tenantId, 'levels', level] as const;
}

export function useLevelEntities(
    tenantId: string | undefined,
    level: LevelNameLocal,
    parentId?: string,
): UseQueryResult<LevelEntityListItemLocal[]> {
    return useQuery({
        queryKey: [...levelKeyPrefix(tenantId, level), parentId ?? null],
        queryFn: () => levelEntityApi.list(tenantId ?? '', level, { parentId }),
        enabled: !!tenantId,
        select: (r) => r.items,
    });
}

export function useCreateLevelEntity(tenantId: string, level: LevelNameLocal) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body: CreateLevelEntityRequestLocal) =>
            levelEntityApi.create(tenantId, level, body, {
                idempotencyKey: generateIdempotencyKey(),
            }),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: levelKeyPrefix(tenantId, level) });
        },
    });
}

export function useUpdateLevelEntity(tenantId: string, level: LevelNameLocal) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, body }: { id: string; body: UpdateLevelEntityRequestLocal }) =>
            levelEntityApi.update(tenantId, level, id, body),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: levelKeyPrefix(tenantId, level) });
        },
    });
}

export function useDeleteLevelEntity(tenantId: string, level: LevelNameLocal) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => levelEntityApi.delete(tenantId, level, id),
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: levelKeyPrefix(tenantId, level) });
        },
    });
}
